import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Button, Input, Select, Space, message, Popconfirm, Tooltip, Upload, Modal } from "antd";
import { 
  PlusOutlined, ReloadOutlined, DownloadOutlined, UploadOutlined, 
  FileTextOutlined, DeleteOutlined as BulkDeleteOutlined,
  EyeOutlined, EditOutlined, DeleteOutlined, SearchOutlined, KeyOutlined,
} from "@ant-design/icons";
import ModuleHeader from "../../components/ModuleHeader";
import StatusBadge from "../../components/common/StatusBadge";
import { PartnerLabel } from "../../components/common/DeletedTag";
import RiderFormModal from "./RiderFormModal";
import DocumentApprovalTab from "./DocumentApprovalTab";
import InventoryTab from "./InventoryTab";
import VehiclesTab from "./VehiclesTab";
import useSettingsDropdown from "../../hooks/useSettingsDropdown";
import usePermission from "../../hooks/usePermission";
import { 
  listRiders, deleteRider, downloadTemplate, exportRiders, 
  bulkUploadRiders, bulkDeleteRiders, forgotPasswordRider,
} from "../../services/riders.service";
import dayjs from "dayjs";
import "./DeliveryPartners.css";

const { Search } = Input;

export default function DeliveryPartners() {
  const navigate = useNavigate();
  const { canEdit, canDelete } = usePermission("deliveryPartners");
  const [activeTab, setActiveTab] = useState("riders");
  const [loading, setLoading] = useState(false);
  const [riders, setRiders] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, per_page: 25, total_count: 0 });
  const [filters, setFilters] = useState({});
  const [search, setSearch] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRider, setEditingRider] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [resettingPasswordId, setResettingPasswordId] = useState(null);
  const fileInputRef = useRef(null);

  // Settings dropdowns
  const { options: projectOptions, loading: projectsLoading } = useSettingsDropdown("projects");
  const { options: fleetOptions, loading: fleetsLoading } = useSettingsDropdown("fleet-vehicle-types", {
    filter: (item) => item.category === "fleet",
  });
  const { options: cityOptions, loading: citiesLoading } = useSettingsDropdown("city-zones", {
    filter: (item) => item.entityType === "city",
  });
  const { options: zoneOptions, loading: zonesLoading } = useSettingsDropdown("city-zones", {
    filter: (item) => item.entityType === "zone",
  });

  const tabs = [
    { key: "riders", label: "Riders" },
    { key: "documents", label: "Document Approval" },
    { key: "inventory", label: "Inventory" },
    { key: "vehicles", label: "Vehicles" },
  ];

  const fetchRiders = useCallback(async (page = pagination.page) => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: pagination.per_page,
        search,
        ...filters,
      };
      const response = await listRiders(params);
      setRiders(response.data.records);
      setPagination(response.data.pagination);
    } catch (error) {
      message.error("Failed to load riders");
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.per_page, search, filters]);

  useEffect(() => {
    if (activeTab === "riders") {
      fetchRiders(1);
    }
  }, [activeTab, search, filters, fetchRiders]);

  const handleTableChange = (pag) => {
    fetchRiders(pag.current);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearch = (value) => {
    setSearch(value);
  };

  const handleCreate = () => {
    setEditingRider(null);
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingRider(record);
    setModalVisible(true);
  };

  const handleView = (record) => {
    navigate(`/delivery-partners/${record._id}`);
  };

  const handleDelete = async (id) => {
    try {
      await deleteRider(id);
      message.success("Rider deleted successfully");
      fetchRiders();
    } catch (error) {
      message.error("Failed to delete rider");
    }
  };

  const handleForgotPassword = (record) => {
    Modal.confirm({
      title: "Reset rider password?",
      content: (
        <div>
          <p>
            Reset password for <strong>{record.fullName}</strong> ({record.riderId}) to
            their Civil ID and require a password change on next login.
          </p>
          {record.emailAddress ? (
            <p style={{ marginBottom: 0, color: "#64748b" }}>
              Steps will be emailed to {record.emailAddress}. The email will not include
              the Civil ID.
            </p>
          ) : (
            <p style={{ marginBottom: 0, color: "#b45309" }}>
              This rider has no email on file. You must inform them of the temporary
              password reset yourself.
            </p>
          )}
        </div>
      ),
      okText: "Reset password",
      cancelText: "Cancel",
      onOk: async () => {
        setResettingPasswordId(record._id);
        try {
          const res = await forgotPasswordRider(record._id);
          const payload = res?.data || res;
          const msg =
            payload?.msg ||
            (payload?.hasEmail === false
              ? "Password reset. Rider has no email — please inform them."
              : "Password reset successfully.");
          if (payload?.hasEmail === false || payload?.emailSent === false) {
            message.warning(msg);
          } else {
            message.success(msg);
          }
        } catch (error) {
          message.error(
            error?.errors?.[0]?.msg || error?.message || "Failed to reset password"
          );
          throw error;
        } finally {
          setResettingPasswordId(null);
        }
      },
    });
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await downloadTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "EZYGO_Rider_Bulk_Upload_Template.csv";
      link.click();
      window.URL.revokeObjectURL(url);
      message.success("Template downloaded successfully");
    } catch (error) {
      message.error("Failed to download template");
    }
  };

  const handleExport = async () => {
    try {
      const blob = await exportRiders(filters);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `EZYGO_Riders_Export_${Date.now()}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      message.success("Riders exported successfully");
    } catch (error) {
      message.error("Failed to export riders");
    }
  };

  const handleBulkUpload = async (file) => {
    setUploading(true);
    try {
      const response = await bulkUploadRiders(file);
      
      const { success, failed, errors, total } = response.data;
      
      // Show detailed result modal
      Modal.info({
        title: "Bulk Upload Results",
        width: 600,
        content: (
          <div>
            <p><strong>Total rows:</strong> {total}</p>
            <p style={{ color: "green" }}><strong>✓ Successful:</strong> {success}</p>
            <p style={{ color: "red" }}><strong>✗ Failed:</strong> {failed}</p>
            {errors.length > 0 && (
              <div style={{ marginTop: 16, maxHeight: 300, overflow: "auto" }}>
                <p><strong>Errors:</strong></p>
                {errors.map((err, idx) => (
                  <div key={idx} style={{ marginBottom: 8, fontSize: 12 }}>
                    <strong>Row {err.row}:</strong> {err.errors.join(", ")}
                  </div>
                ))}
              </div>
            )}
          </div>
        ),
      });
      
      fetchRiders();
    } catch (error) {
      message.error("Bulk upload failed");
    } finally {
      setUploading(false);
    }
    
    return false; // Prevent default upload behavior
  };

  const handleBulkDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning("Please select riders to delete");
      return;
    }

    Modal.confirm({
      title: "Bulk Delete Confirmation",
      content: `Are you sure you want to delete ${selectedRowKeys.length} selected rider(s)?`,
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        try {
          const response = await bulkDeleteRiders(selectedRowKeys);
          const { success, failed } = response.data;
          
          message.success(`Deleted ${success} rider(s). ${failed > 0 ? `${failed} failed.` : ""}`);
          setSelectedRowKeys([]);
          fetchRiders();
        } catch (error) {
          message.error("Bulk delete failed");
        }
      },
    });
  };

  const handleModalClose = (refresh) => {
    setModalVisible(false);
    setEditingRider(null);
    if (refresh) {
      fetchRiders();
    }
  };

  const columns = [
    {
      title: "Rider ID",
      dataIndex: "riderId",
      key: "riderId",
      width: 120,
    },
    {
      title: "External ID",
      dataIndex: "externalRiderId",
      key: "externalRiderId",
      width: 140,
      render: (v) => v || "—",
    },
    {
      title: "Name",
      dataIndex: "fullName",
      key: "fullName",
      width: 220,
      render: (name, record) => (
        <PartnerLabel name={name} deleted={record.status === "deleted" || record.isDeleted} />
      ),
    },
    {
      title: "Mobile",
      dataIndex: "mobileNumber",
      key: "mobileNumber",
      width: 150,
    },
    {
      title: "Project",
      dataIndex: ["project", "name"],
      key: "project",
      width: 150,
    },
    {
      title: "Zone",
      dataIndex: ["zone", "name"],
      key: "zone",
      width: 150,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (status) => <StatusBadge status={status} />,
    },
    {
      title: "Date Joined",
      dataIndex: "dateOfJoining",
      key: "dateOfJoining",
      width: 130,
      render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "—"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 160,
      fixed: "right",
      render: (_, record) => {
        const isDeleted = record.status === "deleted" || record.isDeleted;
        return (
          <Space size="small">
            <Tooltip title="View">
              <Button
                type="text"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => handleView(record)}
              />
            </Tooltip>
            {!isDeleted && (
              <>
                {canEdit && (
                  <>
                    <Tooltip title="Edit">
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                      />
                    </Tooltip>
                    <Tooltip title="Forgot Password">
                      <Button
                        type="text"
                        size="small"
                        icon={<KeyOutlined />}
                        loading={resettingPasswordId === record._id}
                        onClick={() => handleForgotPassword(record)}
                      />
                    </Tooltip>
                  </>
                )}
                {canDelete && (
                  <Popconfirm
                    title="Delete rider"
                    description="Are you sure you want to delete this rider?"
                    onConfirm={() => handleDelete(record._id)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Tooltip title="Delete">
                      <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                    </Tooltip>
                  </Popconfirm>
                )}
              </>
            )}
          </Space>
        );
      },
    },
  ];

  const renderContent = () => {
    if (activeTab === "documents") {
      return <DocumentApprovalTab />;
    }

    if (activeTab === "inventory") {
      return <InventoryTab />;
    }

    if (activeTab === "vehicles") {
      return <VehiclesTab />;
    }

    if (activeTab !== "riders") {
      return (
        <div style={{ padding: 24, textAlign: "center", color: "#94a3b8" }}>
          <p>Coming soon</p>
        </div>
      );
    }

    return (
      <div style={{ padding: 24 }}>
        {/* Filters */}
        <div className="riders-filters">
          <Space wrap size="middle" style={{ marginBottom: 16 }}>
            <Search
              placeholder="Search by name, mobile, civil ID, rider ID"
              allowClear
              enterButton={<SearchOutlined />}
              onSearch={handleSearch}
              style={{ width: 320 }}
            />
            <Select
              placeholder="Project"
              allowClear
              loading={projectsLoading}
              options={projectOptions}
              onChange={(value) => handleFilterChange("project", value)}
              style={{ width: 160 }}
            />
            <Select
              placeholder="Fleet Type"
              allowClear
              loading={fleetsLoading}
              options={fleetOptions}
              onChange={(value) => handleFilterChange("fleetType", value)}
              style={{ width: 160 }}
            />
            <Select
              placeholder="City"
              allowClear
              loading={citiesLoading}
              options={cityOptions}
              onChange={(value) => handleFilterChange("city", value)}
              style={{ width: 160 }}
            />
            <Select
              placeholder="Zone"
              allowClear
              loading={zonesLoading}
              options={zoneOptions}
              onChange={(value) => handleFilterChange("zone", value)}
              style={{ width: 160 }}
            />
            <Select
              placeholder="Status"
              allowClear
              options={[
                { label: "Active", value: "active" },
                { label: "Inactive", value: "inactive" },
                { label: "Deleted", value: "deleted" },
              ]}
              onChange={(value) => handleFilterChange("status", value)}
              style={{ width: 120 }}
            />
          </Space>

          {/* Action Buttons */}
          <Space style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", width: "100%" }}>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={() => fetchRiders()}>
                Refresh
              </Button>
              <Tooltip title="Download CSV template for bulk upload">
                <Button icon={<FileTextOutlined />} onClick={handleDownloadTemplate}>
                  Template
                </Button>
              </Tooltip>
              <Tooltip title="Upload CSV file to create multiple riders">
                <Upload
                  accept=".csv,.xlsx,.xls"
                  beforeUpload={handleBulkUpload}
                  showUploadList={false}
                >
                  <Button icon={<UploadOutlined />} loading={uploading}>
                    Bulk Upload
                  </Button>
                </Upload>
              </Tooltip>
              <Tooltip title="Export filtered riders to CSV">
                <Button icon={<DownloadOutlined />} onClick={handleExport}>
                  Export
                </Button>
              </Tooltip>
            </Space>
            <Space>
              {selectedRowKeys.length > 0 && (
                <Tooltip title={`Delete ${selectedRowKeys.length} selected rider(s)`}>
                  <Button 
                    icon={<BulkDeleteOutlined />} 
                    danger 
                    onClick={handleBulkDelete}
                  >
                    Delete Selected ({selectedRowKeys.length})
                  </Button>
                </Tooltip>
              )}
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreate}
                style={{ background: "#000", borderColor: "#000" }}
              >
                Create
              </Button>
            </Space>
          </Space>
        </div>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={riders}
          loading={loading}
          rowKey="_id"
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
          }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.per_page,
            total: pagination.total_count,
            showSizeChanger: false,
            showTotal: (total) => `Total ${total} riders`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
        />

        {/* Modal */}
        {modalVisible && (
          <RiderFormModal
            visible={modalVisible}
            rider={editingRider}
            onClose={handleModalClose}
          />
        )}
      </div>
    );
  };

  return (
    <div>
      <ModuleHeader
        title="DELIVERY PARTNERS"
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        extra={
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)" }}>
            Limit Usage - 01 / 250
          </div>
        }
      />
      {renderContent()}
    </div>
  );
}
